/**
 * Convert hex color to rgba format
 * @param hex - Hex color string (e.g., "#FF0000")
 * @param alpha - Alpha value between 0 and 1
 * @returns rgba color string
 */
export const hexToRgba = (hex: string, alpha: number): string => {
    // Validate hex string format: must be a string starting with '#' followed by exactly 6 hex characters
    const hexPattern = /^#[0-9A-Fa-f]{6}$/;

    if (typeof hex !== 'string' || !hexPattern.test(hex)) {
        console.warn(`Invalid hex color: "${hex}". Returning transparent fallback.`);
        return 'rgba(0, 0, 0, 0)';
    }

    // Parse R/G/B components
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);

    // Clamp alpha to 0-1 range
    const clampedAlpha = Math.max(0, Math.min(1, alpha));

    return `rgba(${r}, ${g}, ${b}, ${clampedAlpha})`;
};

/**
 * Create a light background version of a primary color
 * @param hex - Hex color string
 * @param alpha - Alpha value for the light version (default: 0.05)
 * @returns rgba color string for light background
 */
export const createLightBackground = (hex: string, alpha: number = 0.05): string => {
    return hexToRgba(hex, alpha);
};

/**
 * Create a glass effect color from a primary color
 * @param hex - Hex color string
 * @param alpha - Alpha value for the glass effect (default: 0.15)
 * @returns rgba color string for glass effect
 */
export const createGlassColor = (hex: string, alpha: number = 0.15): string => {
    return hexToRgba(hex, alpha);
};

/**
 * Create a glass gradient effect from a primary color (Apple-style)
 * Creates a subtle diagonal gradient for light refraction effect
 * @param hex - Hex color string
 * @param alpha - Base alpha value (default: 0.3 for Apple-like appearance)
 * @returns CSS linear-gradient string
 */
export const createGlassGradient = (hex: string, alpha: number = 0.3): string => {
    const baseColor = hexToRgba(hex, alpha);
    const lighterColor = hexToRgba(hex, alpha * 0.6);
    return `linear-gradient(135deg, ${baseColor} 0%, ${lighterColor} 100%)`;
};

/**
 * Create a bubble background color for chat-style messages
 * Blends primary color at 10% opacity over white background
 * This creates a visual effect of "90% white + 10% primary color"
 * @param hex - Hex color string
 * @returns rgba color string for bubble background
 */
export const createBubbleBackground = (hex: string): string => {
    return hexToRgba(hex, 0.1);
};

/**
 * Parse RGB and alpha values from hex or rgba color string
 * @param color - Hex color string (e.g., "#FF0000") or rgba string (e.g., "rgba(255, 0, 0, 0.5)")
 * @returns object with r, g, b values (0-255) and alpha (0-1)
 */
const parseColor = (color: string): { r: number; g: number; b: number; alpha: number } => {
    if (color.startsWith('#')) {
        // Hex format - assume fully opaque
        const r = parseInt(color.slice(1, 3), 16);
        const g = parseInt(color.slice(3, 5), 16);
        const b = parseInt(color.slice(5, 7), 16);
        return { r, g, b, alpha: 1 };
    } else if (color.startsWith('rgba(')) {
        // RGBA format
        const values = color.match(/rgba?\(([^)]+)\)/)?.[1].split(',') || [];
        const r = parseInt(values[0]?.trim() || '0', 10);
        const g = parseInt(values[1]?.trim() || '0', 10);
        const b = parseInt(values[2]?.trim() || '0', 10);
        const alpha = parseFloat(values[3]?.trim() || '1');
        return { r, g, b, alpha };
    } else if (color.startsWith('rgb(')) {
        // RGB format - assume fully opaque
        const values = color.match(/rgb?\(([^)]+)\)/)?.[1].split(',') || [];
        const r = parseInt(values[0]?.trim() || '0', 10);
        const g = parseInt(values[1]?.trim() || '0', 10);
        const b = parseInt(values[2]?.trim() || '0', 10);
        return { r, g, b, alpha: 1 };
    }

    // Fallback to black
    return { r: 0, g: 0, b: 0, alpha: 1 };
};

/**
 * Calculate the luminance of a color with alpha blending over white background
 * @param color - Hex color string (e.g., "#FF0000") or rgba string (e.g., "rgba(255, 0, 0, 0.5)")
 * @returns luminance value between 0 and 1
 */
const getLuminance = (color: string): number => {
    const { r, g, b, alpha } = parseColor(color);

    // For RGBA colors, blend with white background (255, 255, 255)
    // Blended RGB = alpha * foreground + (1 - alpha) * background
    const blendedR = alpha * r + (1 - alpha) * 255;
    const blendedG = alpha * g + (1 - alpha) * 255;
    const blendedB = alpha * b + (1 - alpha) * 255;

    // Normalize to 0-1
    const rNorm = blendedR / 255;
    const gNorm = blendedG / 255;
    const bNorm = blendedB / 255;

    // Convert to linear RGB
    const toLinear = (c: number) => (c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);

    const rLinear = toLinear(rNorm);
    const gLinear = toLinear(gNorm);
    const bLinear = toLinear(bNorm);

    // Calculate luminance using ITU-R BT.709 coefficients
    return 0.2126 * rLinear + 0.7152 * gLinear + 0.0722 * bLinear;
};

/**
 * Determine if a color is light or dark
 * @param color - Color string (hex like "#FF0000", rgba like "rgba(255, 0, 0, 0.5)", or rgb like "rgb(255, 0, 0)")
 * @param threshold - Luminance threshold (default: 0.5)
 * @returns true if color is light, false if dark
 */
export const isLightColor = (color: string, threshold: number = 0.5): boolean => {
    return getLuminance(color) > threshold;
};

/**
 * Get contrasting text color (white or black) for a given background color
 * @param color - Background color string (hex, rgba, or rgb format)
 * @returns "#FFFFFF" for dark backgrounds, "#000000" for light backgrounds
 */
export const getContrastingTextColor = (color: string): string => {
    return isLightColor(color) ? '#000000' : '#FFFFFF';
};
